import {
  getCurrentAccountId,
  useAccounts,
  useCredentialsStore,
  useUserInfoStore,
  getUrlWithSearchParams,
} from "@helium10/re-core";
import type { IAdditionalMenuItem, IAdditionalMenuOptions } from "@helium10/re-features";
import { useProgressLoadStatus } from "@helium10/re-ui-components";
import queryString from "query-string";
import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";

import {
  acceptFeeTermsRoute,
  billingRoute,
  claimCasesRoute,
  inboundRoute,
  documentCenterRoute,
  landingRoute,
  mrsLink,
  onboardingRoute,
  packFeeRoute,
  refundGenieRoute,
} from "@/core/links";
import { useRedirect } from "../../core/hooks/useRedirect";
import { useTranslation } from "../../i18n/useTranslation";
import { ActiveTab } from "../PackFee/store";
import { faUsdCircle, faFileContract } from "@fortawesome/pro-regular-svg-icons";
import {
  connectionsSelector,
  useGetAcceptInfoController,
  useGetServiceInfo,
  useGetSetupInfoController,
  useRefundServiceStore,
} from "@/store/refundServiceStore";
import { useRefundGenieRequest, useRefundGenieStore } from "@/store/refundGenieStore";
import { SallerStatus } from "@/core/helpers/enum.helper";
import { useRefundGeniePermission } from "@/core/hooks/useRefundGenie";

export const useMenu = () => {
  const { t } = useTranslation();
  const { redirect } = useRedirect();
  const location = useLocation();
  const { reportEnable } = useRefundGenieStore((s) => s.permission); // 这个权限来控制侧边栏的样式

  const accountsStatus = useAccounts((s) => s.status); // /api/v1/customers/accounts 结果
  const serviceInfoStatus = useGetServiceInfo((s) => s.status);
  const setupInfoStatus = useGetSetupInfoController((s) => s.status); // /onboarding/info/setup 结果
  const acceptInfoStatus = useGetAcceptInfoController((s) => s.status); // /onboarding/info/accept 结果
  const refundGenieRequestStatus = useRefundGenieRequest((s) => s.status); // /refund-genie/api/report/access 结果,获取 reportEnable

  const data = useRefundServiceStore(connectionsSelector);
  const {
    acceptInfo: { ifAccept },
    setupInfo: { ifSetup },
    // serviceInfo, // 直接数据不使用
  } = useRefundServiceStore();

  // 增加进入RG菜单的权限
  const userInfo = useUserInfoStore((s) => s.userInfo?.results); // 用户基础信息
  const checkLogin = useCredentialsStore((s) => s.checkLogin); // 登录信息

  // RG没有权限点击跳转403页面(跳转逻辑在 AuthGuard 中)
  // 子账号没有RG权限处理逻辑一致,子账号无 RG 权限时需在 RG 流中跳 403, true代表没有RG权限
  const deniedRGPermission = useRefundGeniePermission(); // true代表没有RG权限

  const originItems = useMemo(
    (): IAdditionalMenuItem[] => [
      {
        id: "claim_overview",
        name: t("menu.claim_overview.name"),
        icon: "svg-dashboard",
        link: getUrlWithSearchParams(mrsLink, {}, { hasAccountId: true }),
        highlight: [
          {
            link: mrsLink,
            strict: true,
          },
        ],
      },
      {
        id: "claim_cases",
        name: t("menu.claim_cases.name"),
        icon: "svg-suggestions",
        link: getUrlWithSearchParams(claimCasesRoute, {}, { hasAccountId: true }),
        highlight: [
          {
            link: claimCasesRoute,
            strict: true,
          },
        ],
      },
      {
        id: "inbound",
        name: t("menu.inbound.name"),
        icon: "icon_restock_suggestions",
        isNew: true,
        link: getUrlWithSearchParams(inboundRoute, {}, { hasAccountId: true }),
        highlight: [
          {
            link: inboundRoute,
            strict: true,
          },
        ],
      },
      {
        id: "documentCenter",
        name: t("menu.documentCenter.name"),
        icon: "",
        faIcon: faFileContract,
        isNew: true,
        link: getUrlWithSearchParams(documentCenterRoute, {}, { hasAccountId: true }),
        highlight: [
          {
            link: documentCenterRoute,
            strict: true,
          },
        ],
      },
      {
        id: "packfee",
        name: t("menu.packfee.name"),
        icon: "icon_orders",
        link: getUrlWithSearchParams(packFeeRoute, {}, { hasAccountId: true }),
        highlight: [
          {
            link: packFeeRoute,
            strict: true,
          },
        ],
      },
      {
        id: "billing",
        name: t("menu.billing.name"),
        icon: "icon_financial_events",
        link: getUrlWithSearchParams(billingRoute, {}, { hasAccountId: true }),
        highlight: [
          {
            link: billingRoute,
            strict: true,
          },
        ],
      },
      {
        id: "refundGenie",
        name: t("menu.refundGenie.name"),
        icon: "",
        faIcon: faUsdCircle,
        link: getUrlWithSearchParams(refundGenieRoute, {}, { hasAccountId: true }), // 跳转 `${membersLink}/site/access-denied?id=refundgenie&accountId=${accountId}` 403页面不写在菜单里面,菜单由权限控制,403直接在 AuthGuard 中跳转
        highlight: [
          {
            link: refundGenieRoute,
            strict: true,
          },
          {
            link: `${refundGenieRoute}/:reportId`,
            strict: true,
          },
        ],
      },
    ],
    [t],
  );

  const { setStatus } = useProgressLoadStatus();

  const items = useMemo((): IAdditionalMenuItem[] => {
    // refundGenieRequestStatus loaded 代表 /refund-genie/api/report/access 请求回来, reportEnable 必然有值
    // accountsStatus loaded 代表 /api/v1/customers/accounts 请求回来, 这个结果是为了 deniedRGPermission 有值判断,代表子账号有没有权限
    // 基础检查: userInfo 和 checkLogin 必须存在, accountsStatus 和 refundGenieRequestStatus 必须有结果(loaded代表成功,展示RG菜单,error代表失败不展示RG菜单)
    if (
      !userInfo ||
      !checkLogin ||
      (refundGenieRequestStatus !== "loaded" && refundGenieRequestStatus !== "error") ||
      (accountsStatus !== "loaded" && accountsStatus !== "error")
    ) {
      return [];
    }

    // hasAccessToMrs 为 false 不展示全量菜单单独处理,不需要知道当前处于那个页面,只需要知道有没有 RG 权限即可
    const hasAccessToMrs = !!checkLogin.hasAccessToMrs;
    if (!hasAccessToMrs) {
      // AuthGuard：无 MRS 且非 RG 会导航到 landing；侧栏不出现主流程全量菜单
      // 过滤出 RG 菜单
      let currentItemsPreMrs: IAdditionalMenuItem[] = originItems;
      // 请求失败不展示RG菜单
      if (refundGenieRequestStatus === "error" || accountsStatus === "error") {
        currentItemsPreMrs = originItems.filter((item) => item.id !== "refundGenie");
      }
      // 没有RG权限不展示RG菜单
      if (!reportEnable || deniedRGPermission) {
        currentItemsPreMrs = currentItemsPreMrs.filter((item) => item.id !== "refundGenie");
      }
      // 过滤出 RG 菜单,有可能为空,后续需要判断
      const rgMenuOnly = currentItemsPreMrs.find((item) => item.id === "refundGenie");

      return rgMenuOnly
        ? [
            {
              id: "landing",
              name: t("menu.landing.name"),
              icon: "svg-dashboard",
              link: getUrlWithSearchParams(landingRoute, {}, { hasAccountId: true }),
              highlight: [{ link: landingRoute, strict: false }],
            } as IAdditionalMenuItem,
            rgMenuOnly,
          ]
        : []; // 只展示一个菜单没有意义,返回空数组不展示菜单
    }
    // hasAccessToMrs 为 false 单独处理结束 ---------

    let currentItems: IAdditionalMenuItem[] = originItems;
    // 请求失败不展示RG菜单
    if (refundGenieRequestStatus === "error" || accountsStatus === "error") {
      currentItems = originItems.filter((item) => item.id !== "refundGenie");
    }
    // 没有RG权限不展示RG菜单
    if (!reportEnable || deniedRGPermission) {
      currentItems = currentItems.filter((item) => item.id !== "refundGenie");
    }

    // RG菜单,之前处理过RG菜单,所以这里如果找不到是正常的,后续需要判断该值是否存在
    const refundGenieMenu = currentItems.find((item) => item.id === "refundGenie");

    // serviceInfo「主快照」判定说明 ─────────────────────────────────────
    //
    // 正常首访：必须先等 useGetAcceptInfo / useGetSetup（及 Guard）跑完，
    // 再拉 /onboarding/info，故 serviceInfoStatus 从 init → loading → loaded。
    //
    // 运行中刷新：别处会再次触发 useGetServiceInfo.action()。此时可能出现
    // 「accept/setup 上一轮已是 loaded（store 仍为真），而 serviceInfo 这一轮正在 loading」。
    // 若此时把菜单整块当成「未就绪」([], 或只靠 accept/setup 收窄分支)，侧栏会先消失再回来，体验差。
    //
    // 因此：在满足「accept、setup 均为 loaded」（说明条款/setup 这一轮的数据口径已齐备）的前提下，
    // 若 serviceInfo 仍在 loading，则仍按「已有主数据路径」来算菜单（沿用当前快照 data，
    // 或由 useRefundServiceStore 里尚未被覆盖的上一轮 serviceInfo）；与 AuthGuard「非 loading
    // 不读 service」无冲突——Guard 子树在此时通常已放行，侧栏仅是展示层
    //
    // 若日后希望与 Guard 完全一致「loading 不收口」，应同时调整 Guard 与白屏策略，再收紧此处分支。

    // 请求失败(白屏),不展示菜单
    if (serviceInfoStatus === "error") return [];
    // serviceInfoStatus 结果为 loaded 代表 /onboarding/info 请求回来, 该接口回来那么权限流程中设计的 accept 和 setup 接口必然回来了, 那么此时显示全部的菜单
    // 如果不是loaded,可能是请求还没有到该接口,也有可能永远到不了,在权限的前置判断流程中就结束了
    // (serviceInfoStatus === "loading" && ifAccept) 这个条件是为了在页面使用过程中重新请求 /onboarding/info 接口刷新数据时正常展示菜单使用(例如收起 Set up Account 抽屉会重新请求 /onboarding/info 接口,这个时候不能让菜单闪动)
    if (serviceInfoStatus === "loaded" || (serviceInfoStatus === "loading" && ifAccept)) {
      // 只要有seller是Active状态,代表可以展示所有菜单(RG菜单前面单独处理了)
      if (data.length && data.some(({ statusValue }) => statusValue === SallerStatus.Active)) {
        return currentItems;
      } else {
        // 没有seller是active状态,此时应该进入onboarding页面,判断有没有RG权限,有就展示RG菜单,没有就不展示
        if (refundGenieMenu) {
          return [
            {
              id: "onboarding",
              name: t("menu.onboarding.name"),
              icon: "svg-dashboard",
              link: getUrlWithSearchParams(onboardingRoute, {}, { hasAccountId: true }),
              highlight: [
                {
                  link: onboardingRoute,
                  strict: false,
                },
              ],
            } as IAdditionalMenuItem,
            refundGenieMenu as IAdditionalMenuItem,
          ];
        } else {
          return [];
        }
      }
    } else {
      // 请求失败(白屏),不展示菜单
      if (acceptInfoStatus === "error") return [];
      // 过滤出RG菜单,有可能为undefined,所以后续使用需要控制
      // 判断accept接口结果看展示哪个菜单
      if (acceptInfoStatus === "loaded") {
        if (ifAccept) {
          // 接受条款,需要等待 /onboarding/info 接口回来之后再展示,外层处理了
          // return [];
          // 如果返回为[]空数组会出现菜单存在又消失的效果,这个时候还保持只展示feeTerms菜单,所以这里不返回空数组
          // 只展示当前 feeTerms 菜单,RG菜单单独处理,有就展示RG菜单,没有就不展示
          if (refundGenieMenu) {
            return [
              {
                id: "feeTerms",
                name: t("menu.feeTerms.name"),
                icon: "svg-dashboard",
                link: getUrlWithSearchParams(acceptFeeTermsRoute, {}, { hasAccountId: true }),
                highlight: [
                  {
                    link: acceptFeeTermsRoute,
                    strict: false,
                  },
                ],
              } as IAdditionalMenuItem,
              refundGenieMenu as IAdditionalMenuItem,
            ];
          } else {
            // 没有RG菜单,展示一个菜单没有意义,返回空数组不展示菜单
            return [];
          }
        } else {
          // 请求失败(白屏),不展示菜单
          if (setupInfoStatus === "error") return [];
          // 不接受条款,判断setup接口结果看展示哪个菜单
          if (setupInfoStatus === "loaded") {
            // 设置一个临时menu,根据权限来决定它的值
            let tempMenu: IAdditionalMenuItem;
            if (ifSetup) {
              // !ifAccept && ifSetup
              tempMenu = {
                id: "feeTerms",
                name: t("menu.feeTerms.name"),
                icon: "svg-dashboard",
                link: getUrlWithSearchParams(acceptFeeTermsRoute, {}, { hasAccountId: true }),
                highlight: [
                  {
                    link: acceptFeeTermsRoute,
                    strict: false,
                  },
                ],
              };
            } else {
              // !ifAccept && !ifSetup
              tempMenu = {
                id: "landing",
                name: t("menu.landing.name"),
                icon: "svg-dashboard",
                link: getUrlWithSearchParams(landingRoute, {}, { hasAccountId: true }),
                highlight: [
                  {
                    link: landingRoute,
                    strict: false,
                  },
                ],
              };
            }
            // tempMenu 临时menu已确定,判断有没有RG菜单(权限),有就展示RG菜单
            if (refundGenieMenu) {
              return [tempMenu as IAdditionalMenuItem, refundGenieMenu as IAdditionalMenuItem];
            } else {
              // 没有RG菜单,展示一个菜单没有意义,返回空数组不展示菜单
              return [];
            }
          } else {
            // 数据没回来,只展示当前 setup 菜单,RG菜单单独处理,有就展示RG菜单,没有就不展示
            if (refundGenieMenu) {
              return [
                {
                  id: "landing",
                  name: t("menu.landing.name"),
                  icon: "svg-dashboard",
                  link: getUrlWithSearchParams(landingRoute, {}, { hasAccountId: true }),
                  highlight: [
                    {
                      link: landingRoute,
                      strict: false,
                    },
                  ],
                } as IAdditionalMenuItem,
                refundGenieMenu as IAdditionalMenuItem,
              ];
            } else {
              // 没有RG菜单,展示一个菜单没有意义,返回空数组不展示菜单
              return [];
            }
          }
        }
      } else {
        // 数据没回来,只展示当前 feeTerms 菜单,RG菜单单独处理,有就展示RG菜单,没有就不展示
        if (refundGenieMenu) {
          return [
            {
              id: "feeTerms",
              name: t("menu.feeTerms.name"),
              icon: "svg-dashboard",
              link: getUrlWithSearchParams(acceptFeeTermsRoute, {}, { hasAccountId: true }),
              highlight: [
                {
                  link: acceptFeeTermsRoute,
                  strict: false,
                },
              ],
            } as IAdditionalMenuItem,
            refundGenieMenu as IAdditionalMenuItem,
          ];
        } else {
          // 没有RG菜单,展示一个菜单没有意义,返回空数组不展示菜单
          return [];
        }
      }
    }
  }, [
    // Status 依赖
    accountsStatus, // /api/v1/customers/accounts 结果,
    refundGenieRequestStatus, // /refund-genie/api/report/access 结果,获取 reportEnable
    serviceInfoStatus, // /onboarding/info 结果
    setupInfoStatus, // /onboarding/info/setup 结果
    acceptInfoStatus, // /onboarding/info/accept 结果
    // 基础数据
    userInfo,
    checkLogin,
    deniedRGPermission, // true代表没有RG权限
    // 原始数据
    originItems,
    // serviceInfo, // /onboarding/info 结果,内部使用了data,serviceInfo 直接结果不再使用
    data, // /onboarding/info 结果
    ifAccept, // /onboarding/info/accept 结果
    ifSetup, // /onboarding/info/setup 结果
    reportEnable, // /refund-genie/api/report/access 结果
    // 路由依赖
    // location.pathname, // 不应该依赖路由,路由变化频繁,菜单不应该频繁变化,刷新页面的时候菜单根据权限来展示即可
  ]);

  const handleRouteChange = useCallback(
    (link: string) => {
      if (link.split("?")[0] !== location.pathname) {
        setStatus("start");
      }
      const foundMenuItem = items.find((item) => item.link === link);
      if (foundMenuItem) {
        // packFee 切换tabs使用
        if (link.includes("packFee")) {
          const accountId = queryString.parseUrl(link)?.query?.accountId;
          if (accountId) {
            sessionStorage.setItem(
              "PACKFEETAB",
              JSON.stringify({
                ...JSON.parse(sessionStorage.getItem("PACKFEETAB") ?? "{}"),
                [accountId as string]: ActiveTab.Product,
              }),
            );
          }
        }
        // 所有的菜单以及页面都已迁入MRS项目,判断改为只要有 managed-refund-service 路径就代表在站内使用站内跳转
        if (process.env.NODE_ENV === "development" || link.includes("managed-refund-service")) {
          redirect(link, { accountId: getCurrentAccountId() as string });
        } else return (window.location.href = link);
      }
    },
    [items, redirect],
  );

  const menu: IAdditionalMenuOptions | undefined = useMemo(() => {
    return {
      items: items,
      title: "Managed Refund Service",
      iconsUrl: "/menu-sprite.svg",
      onRouteChange: handleRouteChange,
    };
  }, [handleRouteChange, items]);

  return {
    menu,
  };
};
